import { Vector3 } from 'three';
import { Color } from '@thive/canvas';
import { Flow } from '@thive/fddm-types';

import { useCanvasStore } from '@/modules/canvas/store';
import { elementFactory } from '@/modules/canvas/factories';
import { INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area } from '@/modules/floorplanDesign/types';
import { assertNever } from '@/helpers/utils';
import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';
import { getFlowLineColor, groupFlowsByType } from '@/modules/flowsV2/helpers';
import { useAppStore } from '@/modules/app/store/useAppStore';

type HoverEvent = { elements: readonly { id: string }[] };

const HOVER_LABEL_ID = 'area-text-label';
const OFFSET_LABEL_Y = 0.2;
const LABEL_LENGTH = 1.6;
const LABEL_HORIZONTAL_PADDING = 0.18;

let hoveredAreaId: string | null = null;

const removeAreaHoverLabel = () => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return;

  canvas.removeElements([HOVER_LABEL_ID]);
};

const setElementsStrokeColor = (ids: string[], color: string) => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return;

  canvas.updateElements(
    canvas.getElements(ids).map((element) => ({
      ...element,
      stroke: Color.fromHex(color),
    })),
  );
};

const highlightRelatedFlows = (areaId: string) => {
  const { getRelatedFlows } = useFloorplanDesignStore.getState();
  groupFlowsByType(getRelatedFlows([areaId])).forEach((value: string[], key: Flow['type']) => {
    setElementsStrokeColor(value, modifyColor(getFlowLineColor(key)));
  });
};

const resetRelatedFlows = (areaId: string) => {
  const { selectedIds } = useAppStore.getState();
  if (selectedIds.includes(areaId)) return;

  const { getRelatedFlows } = useFloorplanDesignStore.getState();
  const flows = getRelatedFlows([areaId]);

  groupFlowsByType(flows).forEach((value: string[], key: Flow['type']) => {
    setElementsStrokeColor(value, modifyColor(getFlowLineColor(key), false, '80'));
  });
};

export const getAreaHeight = (area: Area): number => {
  switch (area.areaType) {
    case 'HIGHWAY': {
      const firstSegment = area.segments[0];
      return firstSegment ? firstSegment.leftSideWidth + firstSegment.rightSideWidth : 0;
    }
    case 'MANUAL':
    case 'RACKING':
    case 'SINGULAR':
    case 'STATIONARY':
      return area.rectangle.width;
    default:
      assertNever(area);
  }
};

const addAreaHoverLabel = (e: HoverEvent) => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return;

  const hoveredElement = e.elements[0];
  if (!hoveredElement) return;

  const area = useFloorplanDesignStore.getState().getAreaById(hoveredElement.id);
  if (!area) return;

  if (hoveredAreaId && hoveredAreaId !== area.id) {
    resetRelatedFlows(hoveredAreaId);
  }

  const areaHeight = getAreaHeight(area);
  const centerY = areaHeight / 2 + OFFSET_LABEL_Y;
  const labelWidth = Math.max(LABEL_LENGTH - LABEL_HORIZONTAL_PADDING * 2, 0.001);

  removeAreaHoverLabel();
  highlightRelatedFlows(area.id);
  hoveredAreaId = area.id;
  canvas.updateElements([
    elementFactory.buildTextElement({
      id: HOVER_LABEL_ID,
      name: HOVER_LABEL_ID,
      parentId: area.id,
      text: area.name,
      position: new Vector3(0, centerY, 0),
      fontSize: 260,
      width: labelWidth,
      height: 0.45,
      wrapping: false,
      color: '#1f1f1f',
      backgroundPadding: LABEL_HORIZONTAL_PADDING,
      horizontalAlignment: 'Center',
      verticalAlignment: 'Center',
      alwaysFaceCamera: false,
      interactivity: INTERACTIVITY_OFF,
      tags: [],
      visible: true,
    }),
  ]);
};

export const createAreaHoverLabelHandlers = () => ({
  onElementHoverStart: (e: HoverEvent) => {
    addAreaHoverLabel(e);
  },
  onElementHoverEnd: () => {
    if (hoveredAreaId) {
      resetRelatedFlows(hoveredAreaId);
      hoveredAreaId = null;
    }
    removeAreaHoverLabel();
  },
});
